/**
 * Radio Group Control
 * Radio button group for single selection
 * Performance: Memoized, useCallback + useMemo for options
 */

import React, { useCallback, useMemo, memo } from 'react';
import { WarningIcon } from '@atlas-forms/ui-components-react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';

interface RadioOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

const RadioInput = memo(React.forwardRef<HTMLInputElement, ControlRendererProps>(
  (
    {
      control,
      value,
      onChange,
      onBlur,
      onFocus,
      error,
      touched,
      disabled,
      mode,
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
      },
      [onChange],
    );

    const showError = touched && error;
    const containerClassName = getBaseControlClassName(control, {
      error: showError,
      disabled: disabled || control.disabled,
    });

    // Get options from control config
    const options = useMemo<RadioOption[]>(() => {
      const configOptions = (control.config?.options as RadioOption[]) || [];
      return Array.isArray(configOptions) ? configOptions : [];
    }, [control.config]);

    // Get orientation from config (horizontal or vertical)
    const orientation =
      (control.config?.orientation as 'horizontal' | 'vertical') || 'vertical';
    const groupClassName =
      orientation === 'horizontal'
        ? 'control__radio-group control__radio-group--horizontal'
        : 'control__radio-group control__radio-group--vertical';

    return (
      <div className={containerClassName}>
        {control.label && (
          <fieldset className="control__fieldset">
            <legend className="control__legend">
              <span className="control__label-text">{control.label}</span>
              {control.required && (
                <span className="control__required" aria-label="required">
                  *
                </span>
              )}
            </legend>

            <div className={groupClassName}>
              {options.map((option) => (
                <div key={option.value} className="control__radio-item">
                  <input
                    ref={ref}
                    id={`${control.id}-${option.value}`}
                    type="radio"
                    name={control.id}
                    value={option.value}
                    checked={value === option.value}
                    onChange={handleChange}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    disabled={disabled || control.disabled || option.disabled}
                    className="control__input control__input--radio"
                    aria-invalid={showError ? 'true' : 'false'}
                    aria-required={control.required ? 'true' : 'false'}
                    aria-describedby={
                      showError ? `${control.id}-error` : undefined
                    }
                  />
                  <label
                    htmlFor={`${control.id}-${option.value}`}
                    className="control__label control__label--radio"
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>

            {showError && (
              <span className="control__error-icon" aria-hidden="true">
                <WarningIcon width={14} height={14} />
              </span>
            )}
          </fieldset>
        )}

        {control.helpText && (
          <div id={`${control.id}-help`} className="control__help">
            {control.helpText}
          </div>
        )}

        {showError && (
          <div
            id={`${control.id}-error`}
            className="control__error-message"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>
    );
  },
));

RadioInput.displayName = 'RadioInput';

export default RadioInput;
